import classNames from "classnames";
import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setOnDemandVideo, setSessionBroadcastType } from "../../../../../store/actions/admin/create-event/session";
import { createUploadJob, getChannelUpVideos } from "../../../../../store/actions/admin/videos";
import { EventsState } from "../../../../../store/types";
import { GetDefaultVideo } from "../../../../../store/utils/create-event";
import { HomepageVideo, CreateVideo, SessionTypesEnum } from "../../../../../types/working-model";
import CustomHTML from "../../../../general-ui/edit-html/edit-html";
import Icon, { ICONS, COLORS } from "../../../../general-ui/icon";
import Modal from "../../../../general-ui/modal/modal";
import TextInput from "../../../../general-ui/text-input/text";
import WaitingIndicator from "../../../../general-ui/waiting-indicator/waiting-indicator";
import GreenroomLogo from "../../../../../images/greenroom-logo.svg";
import { GetSessionRtmp } from "../../../../../connection/sessions";
import { copyStringToClipboard } from "../../../../../utils/utils";
import { ChecklistTitles } from "../../../../../store/reducers/admin/assistant";
import { addChecklistItem } from "../../../../../store/actions/authentication";
import { useParams } from "react-router";

interface IVideoModal {
	open: boolean;
	onClose: () => void;
}

const VideoModal: React.FC<IVideoModal> = ({ open, onClose }: IVideoModal) => {
	const {
		CreateSessionReducer: { workingSession },
		VideosReducer: {
			videos,
			loadingVideos
		},
		AuthReducer: {
			token,
			user
		}
	} = useSelector((state: EventsState) => state);
	const dispatch = useDispatch();
	const [modalOpen, setModalOpen] = useState(false);
	const [workingOnDemandVideo, setWorkingOnDemandVideo] = useState<HomepageVideo | CreateVideo | null>(null);
	const [selectingType, setSelectingType] = useState(false);
	const [visibleVideos, setVisibleVideos] = useState<HomepageVideo[]>([]);
	const [searchTerm, setSearchTerm] = useState('');
	const mounted = useRef(false);
	const [viewRtmps, setViewRtmps] = useState(false);
	const [rtmpsAddress, setRtmpsAddress] = useState('');
	const [streamKey, setStreamKey] = useState('');
	const [loadingRtmps, setLoadingRtmps] = useState(false);
	const [copiedText, setCopiedText] = useState(""); //uses copied string to indicate which text was copied
	const { language }: { language: string } = useParams();

	useEffect(() => {
		if (copiedText) setTimeout(() => setCopiedText(""), 2000);
	}, [copiedText]);

	function selectRecordedVideo() {
		if (workingSession?.session_type === SessionTypesEnum.onDemand)
			if (workingOnDemandVideo) dispatch(setOnDemandVideo(workingOnDemandVideo));

		// ASSISTANT CHECKLIST ITEM: complete users assistant checklist item for updating video settings
		if (
			user &&
			!user.onboarded.includes(ChecklistTitles.updateVideo)
		) dispatch(addChecklistItem(token!, user.id, user.onboarded, ChecklistTitles.updateVideo));

		close();
	}

	function setSelectedType(type: "uploaded" | "embed" | "url" | "recorded") {
		return () => {
			setWorkingOnDemandVideo((workingOnDemandVideo) => {
				if (!workingOnDemandVideo) return null;
				return {
					...GetDefaultVideo(),
					type
				};
			});
			setSelectingType(false);
		};
	}

	function setSelectedBroadcastType(type: 'greenroom' | 'rtmps') {
		return () => {
			dispatch(setSessionBroadcastType(type));
			setSelectingType(false);
		};
	}

	function setSelectedVideo(video: HomepageVideo) {
		return () => {
			setWorkingOnDemandVideo(workingOnDemandVideo => {
				if (!workingOnDemandVideo) return null;

				return {
					...video
				};
			});
		};
	}

	const handleSearch = useCallback(({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(value);
	}, []);

	function clickUpload() {
		dispatch(createUploadJob(() => {
			if (mounted.current) {
				dispatch(getChannelUpVideos(token!, user!.active_channel));
			}
		}));
	}

	function handleVideoUrl({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
		setWorkingOnDemandVideo(workingOnDemandVideo => {
			if (!workingOnDemandVideo) return null;

			return {
				...workingOnDemandVideo,
				source: value
			};
		});
	}

	function handleVideoEmbed(code: string) {
		setWorkingOnDemandVideo(workingOnDemandVideo => {
			if (!workingOnDemandVideo) return null;

			return {
				...workingOnDemandVideo,
				source: code
			};
		});
	}

	async function revealRtmps() {
		setViewRtmps(true);
		setLoadingRtmps(true);
		const {
			rtmps_address,
			stream_key
		} = await GetSessionRtmp(token!, workingSession!.uuid, language);

		setRtmpsAddress(rtmps_address);
		setStreamKey(stream_key);
		setLoadingRtmps(false);
	}

	function copy(str: string) {
		return () => {
			setCopiedText(str);
			copyStringToClipboard(str);
		};
	}

	const getCopyButton = useCallback((text: string) => (
		<button onClick={copy(text)}>
			{copiedText === text
				? <>Copied!</>
				: <><Icon name={ICONS.COPY} size={14} color={COLORS.CYAN} /> Copy</>
			}
		</button>
	), [copiedText]);

	const renderVideoSelect = useMemo(() => {
		if (workingSession?.session_type === SessionTypesEnum.onDemand) {
			if (!workingOnDemandVideo) return null;

			switch (workingOnDemandVideo.type) {
				case 'uploaded':
				case 'recorded': {
					return (
						<>
							<div className="recorded-header">
								<TextInput
									onChange={handleSearch}
									placeholder={'Search video...'}
									icon={ICONS.SEARCH}
									size={'small'}
								/>
								<span>or</span>
								<button className="small" onClick={clickUpload}>Upload video</button>
							</div>
							<div className="recorded-videos">
								{loadingVideos ? (
									<WaitingIndicator />
								) : (
									<>
										{visibleVideos.map(video => (
											<div key={video.video} onClick={setSelectedVideo(video)} className={classNames("recorded-video", { selected: video.source === workingOnDemandVideo.source })}>
												<div style={{ backgroundImage: video.image ? `url(${video.image})` : 'none' }} className="video-banner">
												</div>
												<label>{video.name}</label>
												{video.duration && <span className="duration">{new Date(video.duration * 1000).toISOString().substr(11, 8)}</span>}
											</div>
										))}
									</>
								)}
							</div>
						</>
					);
				}
				case 'url': {
					return (
						<TextInput
							placeholder="Enter video URL"
							label={"Add video URL"}
							onChange={handleVideoUrl}
							style={{ marginTop: 16 }}
							defaultValue={workingOnDemandVideo.source ?? ''}
						/>
					);
				}
				case 'embed': {
					return (
						<div className="video-embed-code">
							<label>Embed Code</label>
							<CustomHTML
								onChange={handleVideoEmbed}
								defaultValue={workingOnDemandVideo.source ?? ''}
							/>
						</div>
					);
				}
			}
		} else {
			switch (workingSession?.broadcast_type) {

				case 'greenroom': {
					return null;
				}
				case 'rtmps': {
					return (
						<div className="recorded-header">
							{viewRtmps ? (
								<>
									{
										loadingRtmps ? (
											<WaitingIndicator />
										) : (
											<div className="rtmp-data-container">
												<div className="rtmp-data">
													<div className="rtmp-data-text">
														<h6>RTMP address</h6>
														<span>{rtmpsAddress}</span>
													</div>
													<div className="copy-button">
														{getCopyButton(rtmpsAddress)}
													</div>
												</div>
												<div className="rtmp-data">
													<div className="rtmp-data-text">
														<h6>RTMP stream key</h6>
														<span>{streamKey}</span>
													</div>
													<div className="copy-button">
														{getCopyButton(streamKey)}
													</div>
												</div>

												{/* 
												
												TODO: figure out what to do with the playback url, where they can preview the stream

												*/}
											</div>
										)
									}
								</>
							) : (
								<div className="view-rtmps-container">
									<button className="primary" style={{ marginBottom: 18 }} onClick={revealRtmps}>Show RTMP Info</button>
								</div>
							)}
						</div>
					);
				}
				default: {
					return <h1>Nothing selected</h1>;
				}

			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [workingOnDemandVideo, visibleVideos, loadingVideos, handleSearch, workingSession, loadingRtmps, viewRtmps, streamKey, rtmpsAddress, getCopyButton, copiedText]);

	const selectedWorkingType = useMemo(() => {
		if (workingSession?.session_type === SessionTypesEnum.onDemand) {
			if (workingOnDemandVideo) {
				switch (workingOnDemandVideo.type) {
					case 'embed':
						return <EmbedIframe />;
					case 'recorded':
					case 'uploaded':
						return <AddVideo />;
					case 'url':
						return <LinkVideo />;
					default:
						return (<></>);
				}
			}
		} else {
			if (workingSession?.broadcast_type === 'greenroom') {
				return <GreenroomStreaming />;
			} else {
				return <RTMPStreaming />;
			}
		}

	}, [workingSession, workingOnDemandVideo]);

	function close() {
		setModalOpen(false);
		onClose();
	}

	useEffect(() => {
		if (modalOpen) {
			dispatch(getChannelUpVideos(token!, user!.active_channel));
		}
	}, [dispatch, modalOpen, token, user]);

	useEffect(() => {
		if (!searchTerm) {
			setVisibleVideos(videos);
		} else {
			setVisibleVideos(videos.filter(video => {
				return video.name.toLowerCase().includes(searchTerm.toLowerCase());
			}));
		}
	}, [videos, searchTerm]);

	useEffect(() => {
		mounted.current = true;

		return () => {
			mounted.current = false;
		};
	}, []);

	useEffect(() => {
		if (open) {
			if (workingSession?.on_demand_video) {
				setWorkingOnDemandVideo(workingSession.on_demand_video);
			} else {
				setWorkingOnDemandVideo(GetDefaultVideo());
			}
			setModalOpen(true);
		}
	}, [open, workingSession]);

	return (
		<Modal
			open={modalOpen}
			onRequestClose={close}
			title={workingSession?.session_type === SessionTypesEnum.broadcast ? "Broadcast Session Stream Settings" : "On-demand Session Stream Settings"}
			cancellable={true}
			closeable={false}
			padTitle={true}
			footer={(
				<>
					<button onClick={close} style={{ marginRight: 16 }}>Cancel</button>
					<button onClick={selectRecordedVideo} className="primary">Done</button>
				</>
			)}
		>
			<div className="add-video-modal">
				<div className="video-type-select">
					<div className={classNames("video-type-selected", { open: selectingType })} onClick={() => setSelectingType(true)}>
						{selectedWorkingType}
						<span className="arrow"><Icon name={ICONS.KEYBOARD_ARROW_DOWN} size={12} color={COLORS.WHITE} /></span>
					</div>
					<div className={classNames("video-type-selector", { open: selectingType })}>
						{workingSession?.session_type === SessionTypesEnum.onDemand ? (
							<>
								<div onClick={setSelectedType('uploaded')} className="select-option">
									<AddVideo />
								</div>
								<div onClick={setSelectedType('url')} className="select-option">
									<LinkVideo />
								</div>
								<div onClick={setSelectedType('embed')} className="select-option">
									<EmbedIframe />
								</div>
							</>
						) : (
							<>
								<div className="select-option" onClick={setSelectedBroadcastType('greenroom')}>
									<GreenroomStreaming />
								</div>
								<div className="select-option" onClick={setSelectedBroadcastType('rtmps')}>
									<RTMPStreaming />
								</div>
							</>
						)}

					</div>
				</div>

				{renderVideoSelect}
			</div>
		</Modal>
	);
};

function AddVideo() {
	return (
		<div className="video-selected-type">
			<div className="icon-gradient">
				<Icon name={ICONS.ADD} size={20} color={COLORS.BLACK} />
			</div>

			<div className="video-selected-info">
				<label>Add Video</label>
				<span>Add video from content gallery or upload .mp4 files up to 5 GB</span>
			</div>
		</div>
	);
}

function LinkVideo() {
	return (
		<div className="video-selected-type">
			<div className="icon-gradient">
				<Icon name={ICONS.PAPERCLIP} size={20} color={COLORS.BLACK} />
			</div>

			<div className="video-selected-info">
				<label>Link video</label>
				<span>Use YouTube, Vimeo, Wistia, Mixcloud, Dailymotion, or Twitch link</span>
			</div>
		</div>
	);
}

function EmbedIframe() {
	return (
		<div className="video-selected-type">
			<div className="icon-gradient">
				<Icon name={ICONS.CODE} size={20} color={COLORS.BLACK} />
			</div>

			<div className="video-selected-info">
				<label>Embedded Iframe</label>
				<span>Copy & paste the code of embed HTML code</span>
			</div>
		</div>
	);
}

function GreenroomStreaming() {
	return (
		<div className="video-selected-type">
			<div className="icon-greenroom">
				<img src={GreenroomLogo} alt="greenroom" />

			</div>
			<div className="video-selected-info">
				<label>Greenroom streaming</label>
				<span>Stream the session directly to Events</span>
			</div>
		</div>
	);
}

function RTMPStreaming() {
	return (
		<div className="video-selected-type">
			<div className="icon-gradient">
				<Icon name={ICONS.CAMERA_ON} size={20} color={COLORS.BLACK} />
			</div>

			<div className="video-selected-info">
				<label>RTMP streaming</label>
				<span>Stream from the software of your choice</span>
			</div>
		</div>
	);
}

export default VideoModal;